import { type CompetitorsMediaInvestmentDataKeys } from '@/dashboards/CompetitorsMediaInvestment/api';
import type { RowValueType } from '@/dashboards/CompetitorsMediaInvestment/types';
import moment from 'moment';

export interface DateRange {
    startDate?: string;
    endDate?: string;
}

export interface CompetitorSpendTotals {
    primary: number;
    comparison: number;
}

/**
 * Sums the competitor investment across every row whose date falls inside the
 * `[startDate, endDate]` range (inclusive). `modelCompetitorsMediaInvestmentOptions`
 * returns all periods with no date filtering, so the range filter lives here —
 * mirroring the date-window logic in `groupCompetitorsMediaInvestmentData`.
 */
export const sumInvestmentInRange = (
    rows: RowValueType | undefined,
    fieldKeys: CompetitorsMediaInvestmentDataKeys | undefined,
    range: DateRange | undefined,
): number => {
    if (!rows || !fieldKeys || !range) {
        return 0;
    }

    const hasStart = Boolean(range.startDate);
    const hasEnd = Boolean(range.endDate);

    if (!hasStart && !hasEnd) {
        return rows.reduce((acc, row) => {
            const value = Number(row[fieldKeys.investment]?.value.raw);
            return acc + (isNaN(value) ? 0 : value);
        }, 0);
    }

    const startUtc = hasStart
        ? moment.utc(range.startDate, 'DD-MM-YYYY')
        : null;
    const endUtc = hasEnd ? moment.utc(range.endDate, 'DD-MM-YYYY') : null;

    return rows.reduce((acc, row) => {
        const dateString = row[fieldKeys.date]?.value.formatted;
        if (!dateString) {
            return acc;
        }

        const dateUtc = moment.utc(dateString, 'DD-MM-YYYY');

        if (startUtc && dateUtc.isBefore(startUtc, 'day')) {
            return acc;
        }
        if (endUtc && dateUtc.isAfter(endUtc, 'day')) {
            return acc;
        }

        const value = Number(row[fieldKeys.investment]?.value.raw);
        return acc + (isNaN(value) ? 0 : value);
    }, 0);
};

/**
 * Computes primary and comparison competitor spend totals from investment rows and field keys.
 */
export const calculateCompetitorSpendTotals = (
    rows: RowValueType | undefined,
    fieldKeys: CompetitorsMediaInvestmentDataKeys | undefined,
    primaryRange: DateRange | undefined,
    comparisonRange: DateRange | undefined,
): CompetitorSpendTotals => ({
    primary: sumInvestmentInRange(rows, fieldKeys, primaryRange),
    comparison: comparisonRange
        ? sumInvestmentInRange(rows, fieldKeys, comparisonRange)
        : 0,
});
